import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import ChevronRightIcon from '~/components/Icons/svgs/ChevronRightIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'

import ActivityPerformanceViewer from '~/statistics-v2/components/ActivityPerformanceViewer'
import BadgeCard from '~/statistics-v2/components/BadgeCard'
import MustKnowPerformanceViewer from '~/statistics-v2/components/MustKnowPerformanceViewer'
import ProgressCard from '~/statistics-v2/components/ProgressCard'
import { VideoWatchTimeCard } from '~/statistics-v2/components/VideoWatchTimeCard'
import {
  LearningActivityStructure,
  LevelStats,
  MustKnowTopicProgress,
  PracticeActivityStructure,
  SubtopicStats,
  SubtopicTime,
} from '~/statistics-v2/types/statistics.types'
import { calculateProgress, getOverallGrade } from '~/statistics-v2/utils/statistics.utils'
import { AppLanguage } from '~/types/locale'

interface SubTopicWrapperProps {
  countryCode: string
  shouldExpand?: boolean
  isSmallScreen?: boolean
  mustKnowsProgress: MustKnowTopicProgress[] | undefined
  subTopic: SubtopicStats & {
    levels: {
      [levelId: string]: LevelStats
    }
  }
  /**
   * Every subtopic's time, keyed by view key — this component picks its own entry out of it.
   *
   * ⚠ The whole map rather than one subtopic's figures on purpose: `path` below is the key, so the
   * one place that knows how a subtopic's view key is spelled stays the one place that builds it.
   * Absent while the request is in flight, and absent for a subtopic with no history — both read 0m.
   */
  subtopicTimes?: Record<string, SubtopicTime>
}

export default function SubTopicWrapper({
  subTopic,
  shouldExpand,
  isSmallScreen,
  countryCode,
  mustKnowsProgress,
  subtopicTimes,
}: SubTopicWrapperProps): React.ReactElement {
  const { t } = useTranslation('statisticsV2')
  const [isExpanded, setIsExpanded] = useState(shouldExpand ?? false)
  const [learningActivities, setLearningActivities] = useState<LearningActivityStructure[]>([])
  const [practiceActivities, setPracticeActivities] = useState<
    Array<{ name: string; activitySets: Array<{ name: string; details: PracticeActivityStructure }> }>
  >([])
  const router = useRouter()
  useEffect(() => {
    setIsExpanded(shouldExpand ?? false)
  }, [shouldExpand])

  const totalQuestions = (subTopic.totalQuestionsAttempted || 0) + (subTopic.totalMustKnowQuestionsAttempted || 0)

  const accuracy = (subTopic.correctQuestions / subTopic.totalQuestions) * 100 || 0

  const grade = getOverallGrade(accuracy, router.locale as AppLanguage)
  const levelsProgress = calculateProgress(subTopic.completedLevels, subTopic.totalLevels)

  const path = `/curriculum/v2/grade-${countryCode}/${subTopic.gradeLevel}/${subTopic.subjectOrderNumber}/${subTopic.subTopicLevlNo}`

  useEffect(() => {
    const videos = Object.values(subTopic.levels ?? {}).flatMap((level) =>
      Object.values(level.details?.learningActivities ?? {}),
    )

    const activities = Object.values(subTopic.levels ?? {}).flatMap((level) => ({
      name: level.name as string,
      activitySets: Object.values(level.details?.practiceActivities ?? {}).map((details) => ({
        name: level.name as string,
        url:
          Object.keys(level.details?.learningActivities ?? {}).length > 0
            ? Object.keys(level.details?.learningActivities ?? {})[0].replace('_video', '')
            : '',
        details,
      })),
    }))

    setLearningActivities(videos)
    setPracticeActivities(activities)
  }, [subTopic])

  const expandedHeightClass = isExpanded ? 'max-h-[10000px] opacity-100' : 'max-h-84 lg:max-h-60'

  return (
    <div className="bg-purple-darker/40 border border-white/10 rounded-3 py-4 px-3 lg:px-5 flex flex-col gap-2 shadow-2xl w-full">
      <div className="flex justify-between items-center">
        <h2 className="text-white text-xl lg:text-3xl font-extrabold">{subTopic.name}</h2>
        <button
          className={`hover:opacity-70 duration-300 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <SVGIcon icon={ChevronRightIcon} pathClassName="text-white" size={16} />
        </button>
      </div>

      <div className={`overflow-hidden transition-all duration-1000 ease-in-out ${expandedHeightClass}`}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 items-start">
            <BadgeCard
              isSlim={isSmallScreen}
              srcKey="icons/products/test_prep_badge.png"
              subTitle={t('thisSubtopic')}
              title={t('currently')}
              value={grade}
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            <ProgressCard isProgress title={t('subtopicProgress')} value={levelsProgress} />
            <ProgressCard title={t('levelsCleared')} value={`${subTopic.completedLevels} / ${subTopic.totalLevels}`} />
            <ProgressCard title={t('accuracyAverage')} value={accuracy > 0 ? `${Math.round(accuracy)}%` : null} />
            <ProgressCard title={t('totalActivities')} value={totalQuestions} />
          </div>

          {mustKnowsProgress && mustKnowsProgress.length > 0 && (
            <MustKnowPerformanceViewer mustKnowsProgress={mustKnowsProgress} />
          )}
          <VideoWatchTimeCard
            subTopicPath={path}
            videos={learningActivities}
            videoWatchTime={subtopicTimes?.[path]?.videoWatchTime ?? 0}
          />
          <ActivityPerformanceViewer
            activities={practiceActivities.filter((activity) => activity.activitySets.length > 0)}
            subTopicPath={path}
          />
        </div>
      </div>
    </div>
  )
}
